import { keyBy } from 'lodash-es'
import { isCurrencyValueArray } from '@/helpers/objects'
import { useUserStore } from '@/stores/user'
import type { CurrencyPrice, CurrencyValue } from '@/types/services/common'
import type {
  AssetCustomSettings,
  TargetCurrency,
} from '@/types/services/assets'
import type { PricesColumnValue } from '@/types/components/table'

export type CurrenciesValues = {
  fiat: number | null
  crypto: number | null
}

export type TargetCurrencyValues = {
  fiat: TargetCurrency | null
  crypto: TargetCurrency | null
}

export type TotalsColumnValues = {
  total: PricesColumnValue<number | null>
  net_total: PricesColumnValue<number | null>
  market_price_total: PricesColumnValue<number | null>
}

export function useValues() {
  const userStore = useUserStore()

  function getValueFromCurrencyObject(
    obj: CurrencyValue | CurrencyPrice
  ): number | null {
    if ('value' in obj) {
      return obj.value
    }
    return obj.price
  }

  function findValueByCurrencyId(
    values: CurrencyValue[] | CurrencyPrice[],
    currencyId: string
  ): number | null {
    const value = values.find((v) => v.currency_id === currencyId)
    return value ? getValueFromCurrencyObject(value) : null
  }

  function findFiatValue(values: CurrencyValue[] | CurrencyPrice[]) {
    if (!userStore.currencyFiat) {
      return null
    }
    return findValueByCurrencyId(values, userStore.currencyFiat.id)
  }

  function findCryptoValue(values: CurrencyValue[] | CurrencyPrice[]) {
    if (!userStore.currencyCrypto) {
      return null
    }
    return findValueByCurrencyId(values, userStore.currencyCrypto.id)
  }

  function findCurrenciesValues(values: CurrencyValue[] | CurrencyPrice[]) {
    const valuesMap = keyBy<CurrencyValue | CurrencyPrice>(
      values,
      'currency_id'
    )

    const foundValues: CurrenciesValues = {
      fiat: null,
      crypto: null,
    }

    const fiatId = userStore.currencyFiat?.id
    if (fiatId && fiatId in valuesMap) {
      foundValues.fiat = getValueFromCurrencyObject(valuesMap[fiatId])
    }

    const cryptoId = userStore.currencyCrypto?.id
    if (cryptoId && cryptoId in valuesMap) {
      foundValues.crypto = getValueFromCurrencyObject(valuesMap[cryptoId])
    }

    return foundValues
  }

  function findTargetCurrencies(tc: TargetCurrency[]) {
    const tcMap = keyBy(tc, 'currency_id')

    const foundValues: TargetCurrencyValues = {
      fiat: null,
      crypto: null,
    }

    if (userStore.currencyFiat) {
      foundValues.fiat = tcMap[userStore.currencyFiat.id] ?? null
    }

    if (userStore.currencyCrypto) {
      foundValues.crypto = tcMap[userStore.currencyCrypto.id] ?? null
    }

    return foundValues
  }

  function countObjectPropertySum<T>(
    objects: T[],
    property: keyof T,
    type: 'fiat' | 'crypto' | 'custom',
    customCurrencyId?: string
  ): number {
    let findValueFn: (
      values: CurrencyValue[] | CurrencyPrice[],
      currencyId?: string
    ) => number | null

    if (type === 'fiat') {
      findValueFn = findFiatValue
    } else if (type === 'crypto') {
      findValueFn = findCryptoValue
    } else {
      if (!customCurrencyId) {
        throw new Error('customCurrencyId is required for custom type')
      }
      findValueFn = (values) => findValueByCurrencyId(values, customCurrencyId)
    }

    let sum = 0

    for (const obj of objects) {
      const value = isCurrencyValueArray(obj[property])
        ? (findValueFn(obj[property]) ?? 0)
        : 0
      sum += value
    }

    return sum
  }

  function countTotal(
    values: CurrenciesValues,
    amount: number
  ): CurrenciesValues {
    return {
      fiat: values.fiat !== null ? amount * values.fiat : null,
      crypto: values.crypto !== null ? amount * values.crypto : null,
    }
  }

  function findTotalsColumnValues(
    target_currencies: TargetCurrency[],
    amount: number,
    net_total_sign: number,
    custom_settings: AssetCustomSettings | null,
    parentCustomSettings?: AssetCustomSettings | null
  ): TotalsColumnValues {
    const _net_total_sign = custom_settings ? 1 : net_total_sign
    const tc_values = findTargetCurrencies(target_currencies)

    const total: PricesColumnValue<number | null> = {
      fiat: tc_values.fiat?.price ?? null,
      crypto: tc_values.crypto?.price ?? null,
    }

    const net_total: PricesColumnValue<number | null> = {
      fiat:
        tc_values.fiat != null ? tc_values.fiat.price * _net_total_sign : null,
      crypto:
        tc_values.crypto != null
          ? tc_values.crypto.price * _net_total_sign
          : null,
    }

    const market_price_total = {
      fiat: tc_values.fiat
        ? tc_values.fiat.market_price_per_unit * amount
        : null,
      crypto: tc_values.crypto
        ? tc_values.crypto.market_price_per_unit * amount
        : null,
    }

    const priceCoefficient = tc_values.fiat
      ? tc_values.fiat.market_price_per_unit / tc_values.fiat.price_per_unit
      : 1

    const net_total_market_price = {
      fiat: net_total.fiat != null ? net_total.fiat * priceCoefficient : null,
      crypto:
        net_total.crypto != null ? net_total.crypto * priceCoefficient : null,
    }

    if (custom_settings != null || parentCustomSettings != null) {
      const customUnitPrices = {
        fiat: tc_values.fiat?.price_per_unit ?? null,
        crypto: tc_values.crypto?.price_per_unit ?? null,
      }

      total.marketPrices = market_price_total
      total.unitPrices = customUnitPrices
      total.isCustom = custom_settings != null ? 'Secondary' : 'Primary'
      net_total.marketPrices = net_total_market_price
      net_total.unitPrices = customUnitPrices
      net_total.isCustom = custom_settings != null ? 'Secondary' : 'Primary'
    }

    return {
      total,
      net_total,
      market_price_total,
    }
  }

  return {
    findValueByCurrencyId,
    findFiatValue,
    findCryptoValue,
    findCurrenciesValues,
    findTargetCurrencies,
    countObjectPropertySum,
    countTotal,
    findTotalsColumnValues,
  }
}
